import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const fmt = (ts) => ts ? new Date(ts).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024)    return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

const DOC_TYPE_LABELS = {
  pod:          'Proof of Delivery',
  bol:          'Bill of Lading',
  lumper:       'Lumper Receipt',
  scale_ticket: 'Scale Ticket',
  other:        'Document',
};

const DOC_TYPE_ICON = {
  pod:          'fa-camera',
  bol:          'fa-file-contract',
  lumper:       'fa-receipt',
  scale_ticket: 'fa-weight-hanging',
  other:        'fa-file-alt',
};

const InfoRow = ({ label, value }) => (
  <div>
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <p className="mt-0.5 text-foreground text-sm truncate">{value || '—'}</p>
  </div>
);

const LoadDetailModal = ({ load, onClose, fetchWithAuth }) => {
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [previewDoc, setPreviewDoc] = useState(null);  // { meta, src } | null
  const [previewLoading, setPreviewLoading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchWithAuth(`${BACKEND_URL}/api/bookings/${load.id}/documents`);
        if (res.ok) {
          const data = await res.json().catch(() => []);
          setDocuments(Array.isArray(data) ? data : []);
        }
      } catch {}
      finally { setDocsLoading(false); }
    })();
  }, [load.id]);

  const handleViewDoc = async (doc) => {
    setPreviewLoading(true);
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/drivers/documents/${doc.id}`);
      if (res.ok) {
        const data = await res.json();
        setPreviewDoc({
          meta: data,
          src: `data:${data.content_type};base64,${data.file_data}`,
        });
      }
    } catch {}
    finally { setPreviewLoading(false); }
  };

  const loadRef = load.order_number || load.id?.substring(0, 8).toUpperCase();
  const hasActualTimes = load.actual_pickup_in || load.actual_pickup_out ||
                         load.actual_delivery_in || load.actual_delivery_out;

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-file-alt text-foreground"></i>
              Load #{loadRef}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Load Summary */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <InfoRow label="Status"          value={load.status?.replace(/_/g, ' ')} />
              <InfoRow label="Confirmed Rate"  value={load.confirmed_rate ? `$${load.confirmed_rate.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : null} />
              <InfoRow label="Driver"          value={load.assigned_driver  || 'Not assigned'} />
              <InfoRow label="Carrier"         value={load.assigned_carrier || 'Not assigned'} />
              <InfoRow label="Pickup"          value={load.pickup_location   || [load.pickup_city,   load.pickup_state].filter(Boolean).join(', ')} />
              <InfoRow label="Delivery"        value={load.delivery_location || [load.delivery_city, load.delivery_state].filter(Boolean).join(', ')} />
              <InfoRow label="Pickup Planned"  value={fmt(load.pickup_time_planned)} />
              <InfoRow label="Delivery Planned" value={fmt(load.delivery_time_planned)} />
            </div>

            {/* Actual Times */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2 text-sm">
                <i className="fas fa-clock text-muted-foreground"></i>
                Actual Times
              </h4>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <InfoRow label="Pickup In"     value={fmt(load.actual_pickup_in)} />
                <InfoRow label="Pickup Out"    value={fmt(load.actual_pickup_out)} />
                <InfoRow label="Delivery In"   value={fmt(load.actual_delivery_in)} />
                <InfoRow label="Delivery Out"  value={fmt(load.actual_delivery_out)} />
              </div>
              {!hasActualTimes && (
                <p className="text-xs text-muted-foreground mt-1">
                  Times are recorded automatically as the driver updates their status.
                </p>
              )}
            </div>

            {/* Documents */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2 text-sm">
                <i className="fas fa-folder-open text-muted-foreground"></i>
                Documents
                {!docsLoading && documents.length > 0 && (
                  <span className="ml-auto text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    {documents.length} {documents.length === 1 ? 'file' : 'files'}
                  </span>
                )}
              </h4>

              {docsLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm py-2">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  Loading…
                </div>
              ) : documents.length === 0 ? (
                <div className="flex items-center gap-3 p-4 rounded-lg border border-dashed border-border text-muted-foreground">
                  <i className="fas fa-file text-2xl opacity-30"></i>
                  <div>
                    <p className="text-sm font-medium">No documents uploaded</p>
                    <p className="text-xs mt-0.5">Documents will appear here once the driver uploads them</p>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-center gap-3 px-4 py-3 bg-card hover:bg-muted/50 transition-colors">
                      <i className={`fas ${DOC_TYPE_ICON[doc.doc_type] || 'fa-file-alt'} text-muted-foreground text-base w-5 flex-shrink-0`}></i>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type || 'Document'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {doc.uploader_name || 'Unknown'} · {fmt(doc.uploaded_at)}
                          {doc.file_size ? ` · ${fmtSize(doc.file_size)}` : ''}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewDoc(doc)}
                        disabled={previewLoading}
                        className="flex-shrink-0"
                      >
                        {previewLoading ? (
                          <div className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <><i className="fas fa-eye mr-1.5"></i>View</>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document preview lightbox */}
      {previewDoc && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4"
          onClick={() => setPreviewDoc(null)}
        >
          <div
            className="relative max-w-4xl w-full bg-card rounded-xl shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div>
                <p className="font-semibold text-foreground text-sm">
                  {DOC_TYPE_LABELS[previewDoc.meta.doc_type] || 'Document'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {previewDoc.meta.uploader_name} · {fmt(previewDoc.meta.uploaded_at)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" asChild>
                  <a href={previewDoc.src} download={previewDoc.meta.filename || 'document'}>
                    <i className="fas fa-download mr-1.5"></i>
                    Download
                  </a>
                </Button>
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-muted/80 text-foreground"
                  onClick={() => setPreviewDoc(null)}
                >
                  <i className="fas fa-times text-sm"></i>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-center p-4 bg-black/10 max-h-[75vh] overflow-auto">
              {previewDoc.src.startsWith('data:image/') ? (
                <img
                  src={previewDoc.src}
                  alt={DOC_TYPE_LABELS[previewDoc.meta.doc_type] || 'Document'}
                  className="max-w-full max-h-full object-contain rounded"
                />
              ) : previewDoc.src.startsWith('data:application/pdf') ? (
                <object
                  data={previewDoc.src}
                  type="application/pdf"
                  className="w-full"
                  style={{ height: '65vh' }}
                >
                  <p className="text-muted-foreground text-sm text-center py-8">
                    PDF preview unavailable.{' '}
                    <a href={previewDoc.src} download={previewDoc.meta.filename} className="text-primary underline">
                      Download to view.
                    </a>
                  </p>
                </object>
              ) : (
                <div className="text-center py-10">
                  <i className="fas fa-file text-5xl text-muted-foreground mb-4"></i>
                  <p className="text-muted-foreground text-sm mb-3">{previewDoc.meta.filename}</p>
                  <Button size="sm" asChild>
                    <a href={previewDoc.src} download={previewDoc.meta.filename}>Download File</a>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LoadDetailModal;
